import React, { useCallback, useEffect, useState } from "react";
import { Send } from "lucide-react";
import TextareaAutosize from "react-textarea-autosize";
import { KeyBoardCodeEnter } from "@/types/keybord";
import { usePromptGeneratorContext } from "@/components/feature/promptGenerator/hooks";
import * as analytics from "@/services/analytics";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";

type MessageInputProps = {
  onSendMessage: (message: string) => void;
  isStreaming: boolean;
  isReady: boolean;
};

export const MessageInput: React.FC<MessageInputProps> = React.memo(({ onSendMessage, isStreaming, isReady }) => {
  const [inputMessage, setInputMessage] = useState("");
  const { setRightDrawerContent } = useCurrentPdf();

  const { promptToChat, sendPromptToChat } = usePromptGeneratorContext();

  useEffect(() => {
    if (promptToChat) {
      setRightDrawerContent("promptGenerator");
      onSendMessage(promptToChat);
      sendPromptToChat(null);
    }
  }, [promptToChat, onSendMessage, setRightDrawerContent, sendPromptToChat]);

  const handleSendMessage = useCallback(() => {
    const prompt = inputMessage.trim();
    if (prompt) {
      onSendMessage(prompt);
      analytics.promptEnter(prompt);
      setInputMessage("");
    }
  }, [inputMessage, onSendMessage]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.code === KeyBoardCodeEnter && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-grow items-center rounded-xl bg-white px-4 py-2">
      <TextareaAutosize
        value={inputMessage}
        onChange={e => setInputMessage(e.target.value)}
        onKeyDown={handleKeyPress}
        className="flex-grow resize-none border-none py-2 focus:outline-none focus:ring-0"
        placeholder="Type your message here..."
        rows={1}
        maxRows={5}
      />
      <button
        onClick={handleSendMessage}
        className="ml-4 rounded-full bg-indigo-500 p-2 text-white hover:bg-indigo-600"
        disabled={isStreaming || !isReady}
      >
        <Send size={15} />
      </button>
    </div>
  );
});
